import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { createRevisionPatch, QUICK_REVISION_PROMPTS } from "@/lib/revisionEngine";
import { cn } from "@/lib/utils";
import { Activity, CheckCircle2, RefreshCw, Sparkles, X } from "lucide-react";
import { toast } from "sonner";

const PHASES = ["Reading feedback", "Rewriting", "Quality check"];

// One simple "ask the agent to change this" surface. Type an instruction and
// Send (a revision), or hit "Fresh take" for a new angle. No mode toggle — the
// button you press is the mode.
export function RevisionConsole({
  asset,
  onApply,
  onRevise,
  onCancel,
  dark = false,
  allowScope = false,
  defaultScope = "single",
  className,
}) {
  const [instruction, setInstruction] = useState("");
  const [scope, setScope] = useState(defaultScope);
  const [running, setRunning] = useState(null); // null | "revise" | "regenerate"
  const [phase, setPhase] = useState(-1);

  useEffect(() => {
    setScope(defaultScope);
  }, [defaultScope, asset?.id]);

  const run = async (mode) => {
    if (running) return;
    const prompt = instruction.trim() || (mode === "regenerate" ? "Regenerate with a fresh angle" : "Make this sharper");
    setRunning(mode);
    setPhase(0);
    window.setTimeout(() => setPhase(1), 420);
    window.setTimeout(() => setPhase(2), 840);
    try {
      if (onRevise) {
        const res = await onRevise({ instruction: prompt, mode, scope });
        if (res && res.ok === false) {
          toast.error(`Revision failed: ${res.message || "try again"}`);
          return;
        }
      } else {
        await new Promise((r) => setTimeout(r, 900));
        const patch = createRevisionPatch(asset, { instruction: prompt, mode, source: instruction.trim() ? "manual" : "default" });
        onApply?.(patch, { instruction: prompt, mode, scope });
      }
      setInstruction("");
      toast.success(mode === "regenerate" ? "Fresh variation ready for review" : "Revision ready for review");
    } finally {
      setRunning(null);
      setPhase(-1);
    }
  };

  const panelClass = dark
    ? "border-white/10 bg-white/[0.055] text-white"
    : "border-slate-200 bg-white text-slate-950";
  const inputClass = dark
    ? "border-white/10 bg-[#0C0F0E]/60 text-white placeholder:text-slate-500"
    : "border-slate-200 bg-slate-50 text-slate-950 placeholder:text-slate-400";
  const muted = dark ? "text-slate-400" : "text-slate-500";
  const chipClass = dark
    ? "border-white/10 bg-white/[0.06] text-slate-200 hover:bg-white/[0.10]"
    : "border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100 hover:text-slate-950";

  return (
    <div className={cn("rounded-lg border p-3", panelClass, className)} data-testid={`revision-console-${asset?.id || "asset"}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.16em]">
            <Sparkles className={cn("h-3.5 w-3.5", dark ? "text-[color:var(--text-1)]" : "text-[color:var(--accent-blue)]")} />
            Ask the agent
          </p>
          <p className={cn("mt-1 text-[12px]", muted)}>Tell it how to change this post — or get a fresh take. You can also just edit the text yourself above.</p>
        </div>
        {onCancel && (
          <Button type="button" size="icon" variant="ghost" onClick={onCancel} className={cn("h-7 w-7", dark && "text-slate-300 hover:bg-white/10 hover:text-white")} data-testid="revision-console-close">
            <X className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      {allowScope && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className={cn("text-[11px]", muted)}>Apply to:</span>
          <button type="button" onClick={() => setScope("single")} className={cn("rounded-md border px-2.5 py-1 text-[11px] font-medium transition-colors", scope === "single" ? "border-emerald-500 bg-emerald-50 text-emerald-700" : chipClass)} data-testid="revision-scope-single">
            This post
          </button>
          <button type="button" onClick={() => setScope("all")} className={cn("rounded-md border px-2.5 py-1 text-[11px] font-medium transition-colors", scope === "all" ? "border-emerald-500 bg-emerald-50 text-emerald-700" : chipClass)} data-testid="revision-scope-all">
            All posts in run
          </button>
        </div>
      )}

      <div className="mt-3 space-y-1.5">
        <Label className={cn("text-[11px] uppercase tracking-[0.16em]", muted)}>Instruction <span className="normal-case opacity-70">(optional for a fresh take)</span></Label>
        <Textarea
          value={instruction}
          onChange={(event) => setInstruction(event.target.value)}
          rows={3}
          placeholder="e.g. Make this more direct, less hype, and add a stronger CTA."
          className={cn("resize-none", inputClass)}
          data-testid="revision-instruction-input"
        />
      </div>

      <div className="mt-2 flex flex-wrap gap-2">
        {QUICK_REVISION_PROMPTS.map((prompt) => (
          <button
            key={prompt}
            type="button"
            onClick={() => setInstruction((current) => current ? `${current}; ${prompt}` : prompt)}
            className={cn("rounded-md border px-2.5 py-1 text-[11px] transition-colors", chipClass)}
            data-testid={`revision-chip-${prompt.toLowerCase().replace(/\s+/g, "-")}`}
          >
            {prompt}
          </button>
        ))}
      </div>

      {running && (
        <div className={cn("mt-3 grid grid-cols-3 gap-1.5 rounded-md border p-2", dark ? "border-white/10 bg-[#0C0F0E]/40" : "border-slate-200 bg-slate-50")} data-testid="revision-agent-phases">
          {PHASES.map((label, index) => {
            const done = phase > index;
            const active = phase === index;
            return (
              <span key={label} className={cn("inline-flex items-center justify-center gap-1 rounded px-1.5 py-1 text-[10.5px] font-medium", done ? "text-emerald-600" : active ? "text-[color:var(--accent-blue)]" : muted)}>
                {done ? <CheckCircle2 className="h-3 w-3" /> : <Activity className={cn("h-3 w-3", active && "animate-pulse")} />}
                {label}
              </span>
            );
          })}
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          onClick={() => run("revise")}
          disabled={Boolean(running)}
          className="gap-1.5"
          data-testid="revision-run-button"
          style={{ backgroundColor: dark ? "rgba(34,211,238,0.16)" : "var(--accent-blue)", color: dark ? "#CFFAFE" : "#FFFFFF" }}
        >
          {running === "revise" ? <Activity className="h-3.5 w-3.5 animate-pulse" /> : <Sparkles className="h-3.5 w-3.5" />}
          {running === "revise" ? "Working…" : "Send to agent"}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => run("regenerate")}
          disabled={Boolean(running)}
          className={cn("gap-1.5", dark && "border-white/15 text-slate-200 hover:bg-white/10")}
          data-testid="revision-regenerate-button"
        >
          {running === "regenerate" ? <Activity className="h-3.5 w-3.5 animate-pulse" /> : <RefreshCw className="h-3.5 w-3.5" />}
          {running === "regenerate" ? "Working…" : "Fresh take"}
        </Button>
        <span className={cn("text-[11px]", muted)}>Fresh take = a new angle on the same idea.</span>
      </div>
    </div>
  );
}
